import { Button } from 'blaze-ui/Button'
import { Upload5 } from 'blaze-ui/icons'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import Styles from './BrandVisualStyleSelection.module.scss'
import cx from 'classnames'
import { FileTrigger } from 'components/FileTrigger/FileTrigger'
import { useImportMediaFiles } from 'hooks/useImportMediaFiles'
import { MediaFile } from 'components/VisualStylePreviewCarousel/VisualStylePreviewCarousel'
import { LoadingEllipses } from 'components/Wizards/Wizard/Form/SectionHeader'
import { useState } from 'react'

export interface SelectionStageProps {
  onNext: (uploadedImage?: MediaFile) => void
  onSelectImage: (image: MediaFile) => void
  onBack: () => void
  isDisabled: boolean
  brandKitId?: number
}

export const SelectionStage = ({ onNext, onSelectImage, onBack, isDisabled, brandKitId }: SelectionStageProps) => {
  const { uploadImageFilesWithoutLoadingToast, isUploading } = useImportMediaFiles(false)
  const [exampleSelected, setExampleSelected] = useState<'person' | 'product' | 'place'>('product')

  const exampleOrigins = {
    person:
      'https://res.cloudinary.com/almanac/image/upload/v1766440487/blaze_assets/virtual_marketer/onboarding/person1_el70oy.png',
    product:
      'https://res.cloudinary.com/almanac/image/upload/v1766440488/blaze_assets/virtual_marketer/onboarding/product1_mnbngo.png',
    place:
      'https://res.cloudinary.com/almanac/image/upload/v1766440488/blaze_assets/virtual_marketer/onboarding/place1_zw12wz.png',
  }

  const exampleGenerated = {
    person:
      'https://res.cloudinary.com/almanac/image/upload/v1766440488/blaze_assets/virtual_marketer/onboarding/person2_dwlv80.png',
    product:
      'https://res.cloudinary.com/almanac/image/upload/v1766440488/blaze_assets/virtual_marketer/onboarding/product2_jctec0.jpg',
    place:
      'https://res.cloudinary.com/almanac/image/upload/v1766440488/blaze_assets/virtual_marketer/onboarding/place2_txo5ma.png',
  }

  return (
    <div className={Styles.selectImageStep}>
      <div className={Styles.leftSide}>
        <div className={Styles.title}>
          <Heading level={1} color="var(--dark-80)">
            Next, pick a cinematic Visual Style for all your content
          </Heading>
          <div className={Styles.description}>
            <Text variant="primary" color="var(--dark-40)">
              Transform all your photos to look like they were shot by a top agency.
            </Text>
          </div>
        </div>
        <div className={Styles.content}>
          <div className={Styles.originImageContainer}>
            <img
              src={exampleOrigins[exampleSelected]}
              alt="demo origin"
              className={Styles.originImage}
              onContextMenu={(e) => e?.preventDefault()}
            />
            <div className={Styles.tryYourOwnButtonContainer}>
              <FileTrigger
                accept={['image/png', 'image/jpeg']}
                allowsMultiple={false}
                onSelect={async (files) => {
                  if (!files) return
                  const mediaFiles = await uploadImageFilesWithoutLoadingToast(Array.from(files), undefined, {
                    brand_kit_id: brandKitId,
                  })
                  const image = {
                    id: mediaFiles?.[0]?.id,
                    url: mediaFiles?.[0]?.url,
                    type: mediaFiles?.[0]?.mime_type?.startsWith('video') ? 'video' : 'image',
                  }
                  onSelectImage(image as MediaFile)
                  onNext(image as MediaFile)
                }}
              >
                <div className={Styles.tryYourOwnButtonContent}>
                  <Button
                    variant="secondary"
                    size="xl"
                    aria-label="try your own"
                    frontIcon={Upload5}
                    isDisabled={isUploading || isDisabled}
                  >
                    {isUploading ? (
                      <>
                        Uploading
                        <LoadingEllipses />
                      </>
                    ) : (
                      'Try with your own photo'
                    )}
                  </Button>
                </div>
              </FileTrigger>
            </div>
            <div className={Styles.examplesContainer}>
              <Text variant="secondary" color="var(--dark-40)">
                Examples
              </Text>
              <div className={Styles.examples}>
                <div
                  className={cx(Styles.example, { [Styles.selected]: exampleSelected === 'product' })}
                  onClick={() => setExampleSelected('product')}
                >
                  Product
                </div>
                <div
                  className={cx(Styles.example, { [Styles.selected]: exampleSelected === 'person' })}
                  onClick={() => setExampleSelected('person')}
                >
                  Person
                </div>
                <div
                  className={cx(Styles.example, { [Styles.selected]: exampleSelected === 'place' })}
                  onClick={() => setExampleSelected('place')}
                >
                  Place
                </div>
              </div>
            </div>
            <div className={cx(Styles.sourceTag, Styles.imageTag)}>Source Image</div>
          </div>
        </div>
        <div className={Styles.footer}>
          <Button variant="tertiary" size="lg" onPress={onBack} isDisabled={isUploading}>
            Back
          </Button>
          <div className={Styles.footerRightButtons}>
            <Button size="lg" variant="secondary" onPress={() => onNext()} isDisabled={isUploading || isDisabled}>
              Continue without your photo
            </Button>
            <FileTrigger
              accept={['image/png', 'image/jpeg']}
              allowsMultiple={false}
              onSelect={async (files) => {
                if (!files) return
                const mediaFiles = await uploadImageFilesWithoutLoadingToast(Array.from(files), undefined, {
                  brand_kit_id: brandKitId,
                })
                const image = {
                  id: mediaFiles?.[0]?.id,
                  url: mediaFiles?.[0]?.url,
                  type: mediaFiles?.[0]?.mime_type?.startsWith('video') ? 'video' : 'image',
                }
                onSelectImage(image as MediaFile)
                onNext(image as MediaFile)
              }}
            >
              <Button
                variant="primary"
                size="lg"
                aria-label="try with your own photo"
                frontIcon={Upload5}
                isDisabled={isUploading || isDisabled}
              >
                {isUploading ? (
                  <>
                    Uploading
                    <LoadingEllipses />
                  </>
                ) : (
                  'Try with your own photo'
                )}
              </Button>
            </FileTrigger>
          </div>
        </div>
      </div>
      <div className={Styles.transformedImageContainer}>
        <img
          src={exampleGenerated[exampleSelected]}
          alt="demo transformed"
          className={cx(Styles.transformedImage, Styles.visible)}
          onContextMenu={(e) => e?.preventDefault()}
        />
        <div className={cx(Styles.imageTag, Styles.generatedTag)}>Generated Image</div>
      </div>
    </div>
  )
}
